import React from 'react';
import Typography from 'components/commercetools-ui/atoms/typography';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useI18n from 'helpers/hooks/useI18n';
import { replaceTemplateString } from 'helpers/utils/replaceTemplateString';
import { Money } from 'types/entity/product';

interface PricesProps {
  discountedPrice?: Money | string;
  price?: Money | string;
  discountPercentage?: number;
  pdpType?: 'custom' | 'standard';
}

const Prices: React.FC<PricesProps> = ({
  price,
  discountedPrice,
  discountPercentage,
  pdpType = 'standard'
}) => {
  const { locale } = useI18n();
  const { formatMessage } = useFormat({ name: 'product' });

  if (!price) return null;

  const formattedPrice =
    typeof price === 'string'
      ? price
      : CurrencyHelpers.formatForCurrency(price, locale);

  const formattedDiscountedPrice =
    discountedPrice &&
    (typeof discountedPrice === 'string'
      ? discountedPrice
      : CurrencyHelpers.formatForCurrency(discountedPrice, locale));

  const isOnSale = Boolean(discountedPrice);

  const label = formatMessage({
    id: 'sale.price',
    defaultMessage: 'Sale Price',
  });

  const discountBadgeTextTemplate = formatMessage({
    id: 'discount.badge',
    defaultMessage: 'Save {percentage}%',
  });
  return (
    <div className="flex flex-col">
      {isOnSale ? (
        <>
          {/* Sale Label */}
          <Typography
            as="p"
            className="mb-8 text-14 font-medium leading-20 text-[#d91e28]"
          >
            {label}
          </Typography>

          {/* Price Row */}
          <div className="flex items-center gap-8">
            {/* Sale Price */}
            <Typography
              as="p"
              className="leading-24 text-24 font-medium text-[#d91e28]"
            >
              {formattedDiscountedPrice}
            </Typography>

            <div className="flex items-center gap-8">
              <Typography
                as="p"
                className="leading-24 text-24 text-[#9e9e9e] line-through"
              >
                {formattedPrice}
              </Typography>

              {discountPercentage && discountPercentage > 0 && (
                <span className="inline-flex h-25 w-69 items-center justify-center gap-10 bg-[#d91e28] p-3 text-12 font-semibold text-white">
                  {replaceTemplateString(discountBadgeTextTemplate, {
                    percentage: Math.round(discountPercentage),
                  })}
                </span>
              )}
            </div>
          </div>
        </>
      ) : (
        <Typography
          as="p"
          className="text-28 font-medium leading-[1.1] tracking-[0.08px] text-[#212121]"
        >
          {formattedPrice}
        </Typography>
      )}
    </div>
  );
};

export default Prices;
